/**
 * AP-32 — **Das Belegwesen als geteilter Vertrag.**
 *
 * Backend, ERP und ein späterer E-Rechnungs-Export lesen dieselbe Wahrheit:
 * welche Belegarten es gibt, was jede kann, welches Recht an ihr hängt, wie ein
 * Positionsentwurf aussieht und wie ein Layout gebaut ist.
 *
 * **Die Fähigkeitstabelle ersetzt dreizehn `if (kind === …)`** (AP-32 3.). Wer
 * wissen will, ob eine Art Preise führt, steuerwirksam ist oder festgeschrieben
 * werden muss, fragt `DOCUMENT_KIND_CAPABILITIES` — Validierung, Oberfläche, PDF
 * und Rechte fragen dieselbe Tabelle.
 *
 * Spezifikation: docs/Arbeitspakete/AP-32/AP-32_Belegwesen.md
 */

// ─── Arten und Zustände ──────────────────────────────────────────────────────

/** Die dreizehn Belegarten — deckungsgleich mit dem Prisma-Enum `DocumentKind`. */
export const DOCUMENT_KINDS = [
  'OFFER',
  'ORDER_CONFIRMATION',
  'DELIVERY_NOTE',
  'INVOICE',
  'DOWN_PAYMENT_INVOICE',
  'PARTIAL_INVOICE',
  'FINAL_INVOICE',
  'CREDIT_NOTE',
  'CANCELLATION',
  'DUNNING',
  'PURCHASE_ORDER',
  'INQUIRY',
  'CUSTOM',
] as const
export type DocumentKind = (typeof DOCUMENT_KINDS)[number]

export type DocumentDirection = 'SALES' | 'PURCHASE' | 'NEUTRAL'
export const DOCUMENT_STATUSES = ['DRAFT', 'POSTED', 'SENT', 'CANCELLED'] as const
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number]
export const DOCUMENT_PAY_STATUSES = ['NONE', 'OPEN', 'PARTIALLY_PAID', 'PAID', 'OVERDUE'] as const
export type DocumentPayStatus = (typeof DOCUMENT_PAY_STATUSES)[number]
/**
 * `DEDUCTION` (Nachtrag, zweite Runde): ein Abzug an seiner Stelle — prozentual
 * (`discountPercent`) oder pauschal (`unitPrice`) auf die Zwischensumme aller
 * zählenden Zeilen darüber, anteilig auf die Steuersätze verteilt.
 */
export const DOCUMENT_LINE_TYPES = ['ARTICLE', 'TEXT', 'TITLE', 'SUBTOTAL', 'DEDUCTION', 'REFERENCE', 'PAGE_BREAK'] as const
export type DocumentLineType = (typeof DOCUMENT_LINE_TYPES)[number]

/** Nachtrag 6: Verrechnungsstand eines Lieferscheins — gerechnet aus den Rechnungszeilen, händisch übersteuerbar. */
export const DELIVERY_BILLING_STATUSES = ['NONE', 'PARTIAL', 'FULL'] as const
export type DeliveryBillingStatus = (typeof DELIVERY_BILLING_STATUSES)[number]
/** Rechnungsarten, deren Zeilen einen Lieferschein verrechnen (über `sourceLineId`). */
export const BILLING_INVOICE_KINDS = ['INVOICE', 'DOWN_PAYMENT_INVOICE', 'PARTIAL_INVOICE', 'FINAL_INVOICE'] as const

/** Anredeform — Textbausteine gibt es je Form; welche gilt, entscheidet der Ansprechpartner. */
export const FORMS_OF_ADDRESS = ['FORMAL', 'INFORMAL'] as const
export type DocumentFormOfAddress = (typeof FORMS_OF_ADDRESS)[number]

/** Arten mit laufender Nummer im Titel („2. Teilrechnung"). */
export const NUMBERED_INSTALLMENT_KINDS = ['DOWN_PAYMENT_INVOICE', 'PARTIAL_INVOICE'] as const

export function belegTitel(kindName: string, installmentNo: number | null | undefined, sprache: string): string {
  if (!installmentNo) return kindName
  return sprache === 'en' ? `${kindName} no. ${installmentNo}` : `${installmentNo}. ${kindName}`
}
export const SUB_POSITION_MODES = ['SUM_OF_CHILDREN', 'OWN_PRICE'] as const
export type SubPositionMode = (typeof SUB_POSITION_MODES)[number]
export const TAX_PROCEDURES = ['STANDARD', 'REVERSE_CHARGE', 'INTRA_EU', 'EXPORT', 'SMALL_BUSINESS', 'EXEMPT'] as const
export type TaxProcedure = (typeof TAX_PROCEDURES)[number]
export const PRICE_MODES = ['NET', 'GROSS'] as const
export type PriceMode = (typeof PRICE_MODES)[number]

/** Ja, nein — oder die Art lässt es zu, verlangt es aber nicht. */
export type Faehigkeit = 'yes' | 'no' | 'optional'

// ─── Rechte ──────────────────────────────────────────────────────────────────

/**
 * Die Rechtegruppen je Belegart (AP-32 10.2). Anzahlungs-, Teil- und
 * Schlussrechnung und das Storno hängen an `invoice` — sie sind Rechnungen, und
 * ein eigenes Recht für die Teilrechnung wäre eine Unterscheidung, die niemand
 * in einer Rolle abbilden will.
 */
export const DOCUMENT_PERMISSION_GROUPS = [
  'offer',
  'order_confirmation',
  'delivery_note',
  'invoice',
  'credit_note',
  'dunning',
  'purchase_order',
  'inquiry',
  'custom_document',
] as const
export type DocumentPermissionGroup = (typeof DOCUMENT_PERMISSION_GROUPS)[number]

export type DocumentVerb = 'read' | 'create' | 'update' | 'delete'

// ─── Die Fähigkeitstabelle (AP-32 4.2) ───────────────────────────────────────

export interface DocumentKindCapabilities {
  /** Abgeleitet, nie getippt (3.1). */
  direction: DocumentDirection
  /** Führt Positionen. `optional` = ein Brief ohne Zeilen ist erlaubt. */
  lines: Faehigkeit
  /** Führt Beträge. `optional` beim Lieferschein und beim individuellen Dokument. */
  prices: Faehigkeit
  /** Trägt einen Steuerausweis und unterliegt der Merkmalsprüfung (7.4). */
  taxable: boolean
  /** Hat einen offenen Betrag, Zahlungen und eine Fälligkeit (8.4). */
  payable: boolean
  /** Vorgabe-Präfix der Nummernreihe — je Mandant änderbar (6.). */
  prefix: string
  permissionGroup: DocumentPermissionGroup
  /** Wer der Empfänger sein muss (AP-31: Kunde/Lieferant). */
  partnerRole: 'customer' | 'supplier' | 'any'
  /**
   * Muss vor dem Versand festgeschrieben werden? Nur das individuelle Dokument
   * nicht — ein Brief darf Entwurf bleiben (9.1).
   */
  postBeforeSend: boolean
  /** Negative Beträge sind der Normalfall (Storno, Gutschrift — 8.3). */
  negative: boolean
  /**
   * Darf der Mandant das Festschreiben für diese Art abschalten (Nachtrag N-10)?
   * Nie bei steuer- oder zahlungswirksamen Arten und nie bei der Mahnung — deren
   * Nummer muss lückenlos sein bzw. setzt erst das Festschreiben die Mahnstufe.
   */
  postingOptional: boolean
}

const verkauf = { direction: 'SALES', partnerRole: 'customer', postBeforeSend: true, negative: false } as const

export const DOCUMENT_KIND_CAPABILITIES: Readonly<Record<DocumentKind, DocumentKindCapabilities>> = {
  OFFER: { ...verkauf, lines: 'yes', prices: 'yes', taxable: false, payable: false, prefix: 'AN', permissionGroup: 'offer', postingOptional: true },
  ORDER_CONFIRMATION: { ...verkauf, lines: 'yes', prices: 'yes', taxable: false, payable: false, prefix: 'AB', permissionGroup: 'order_confirmation', postingOptional: true },
  DELIVERY_NOTE: { ...verkauf, lines: 'yes', prices: 'optional', taxable: false, payable: false, prefix: 'LS', permissionGroup: 'delivery_note', postingOptional: true },
  INVOICE: { ...verkauf, lines: 'yes', prices: 'yes', taxable: true, payable: true, prefix: 'RE', permissionGroup: 'invoice', postingOptional: false },
  DOWN_PAYMENT_INVOICE: { ...verkauf, lines: 'yes', prices: 'yes', taxable: true, payable: true, prefix: 'AZ', permissionGroup: 'invoice', postingOptional: false },
  PARTIAL_INVOICE: { ...verkauf, lines: 'yes', prices: 'yes', taxable: true, payable: true, prefix: 'TR', permissionGroup: 'invoice', postingOptional: false },
  FINAL_INVOICE: { ...verkauf, lines: 'yes', prices: 'yes', taxable: true, payable: true, prefix: 'SR', permissionGroup: 'invoice', postingOptional: false },
  CREDIT_NOTE: { ...verkauf, lines: 'yes', prices: 'yes', taxable: true, payable: true, prefix: 'GS', permissionGroup: 'credit_note', negative: true, postingOptional: false },
  CANCELLATION: { ...verkauf, lines: 'yes', prices: 'yes', taxable: true, payable: true, prefix: 'ST', permissionGroup: 'invoice', negative: true, postingOptional: false },
  // Die Mahnung führt statt Positionen die offenen Rechnungen als REFERENCE-Zeilen.
  DUNNING: { ...verkauf, lines: 'no', prices: 'yes', taxable: false, payable: false, prefix: 'MA', permissionGroup: 'dunning', postingOptional: false },
  PURCHASE_ORDER: { direction: 'PURCHASE', partnerRole: 'supplier', postBeforeSend: true, negative: false, lines: 'yes', prices: 'yes', taxable: false, payable: false, prefix: 'BE', permissionGroup: 'purchase_order', postingOptional: true },
  // Die Preisanfrage fragt Preise ab, sie nennt keine.
  INQUIRY: { direction: 'PURCHASE', partnerRole: 'supplier', postBeforeSend: true, negative: false, lines: 'yes', prices: 'no', taxable: false, payable: false, prefix: 'PA', permissionGroup: 'inquiry', postingOptional: true },
  CUSTOM: { direction: 'NEUTRAL', partnerRole: 'any', postBeforeSend: false, negative: false, lines: 'optional', prices: 'optional', taxable: false, payable: false, prefix: 'DO', permissionGroup: 'custom_document', postingOptional: true },
}

/**
 * Muss dieser Beleg festgeschrieben werden, bevor er unveränderlich ist (N-10)?
 * Die Einstellung zählt nur, wo die Art sie zulässt — sonst immer ja.
 */
export function festschreibenPflicht(kind: DocumentKind, requirePosting: boolean | null | undefined): boolean {
  return DOCUMENT_KIND_CAPABILITIES[kind].postingOptional ? requirePosting !== false : true
}

// ─── Kopffelder je Art (Nachtrag, Abschnitt 2) ───────────────────────────────

/** Kopffelder, die nicht jede Art führt. Maske, Speichern, Übernahme und PDF fragen diese Tabelle. */
export type DocumentHeaderField =
  | 'commission'
  | 'deliveryTerms'
  | 'deliveryDate'
  | 'priceValidity'
  | 'paymentSchedule'
  | 'orderNumber'
  | 'servicePeriod'
  | 'quoteDeadline'
  /** Nachtrag 4: Lieferadresse (Bestellung) bzw. Zustelladresse (Lieferschein) — nur dort gedruckt. */
  | 'deliveryAddress'

const RECHNUNGSFELDER: readonly DocumentHeaderField[] = ['commission', 'orderNumber', 'servicePeriod']

export const DOCUMENT_HEADER_FIELDS: Readonly<Record<DocumentKind, readonly DocumentHeaderField[]>> = {
  OFFER: ['commission', 'deliveryTerms', 'deliveryDate', 'priceValidity', 'paymentSchedule'],
  ORDER_CONFIRMATION: ['commission', 'deliveryTerms', 'deliveryDate', 'priceValidity', 'paymentSchedule', 'orderNumber'],
  DELIVERY_NOTE: [...RECHNUNGSFELDER, 'deliveryAddress'],
  INVOICE: RECHNUNGSFELDER,
  DOWN_PAYMENT_INVOICE: RECHNUNGSFELDER,
  PARTIAL_INVOICE: RECHNUNGSFELDER,
  FINAL_INVOICE: RECHNUNGSFELDER,
  CREDIT_NOTE: RECHNUNGSFELDER,
  CANCELLATION: RECHNUNGSFELDER,
  DUNNING: [],
  PURCHASE_ORDER: ['commission', 'deliveryTerms', 'deliveryDate', 'deliveryAddress'],
  INQUIRY: ['commission', 'quoteDeadline'],
  CUSTOM: [],
}

export function hatKopffeld(kind: DocumentKind, feld: DocumentHeaderField): boolean {
  return DOCUMENT_HEADER_FIELDS[kind].includes(feld)
}

/**
 * Die Preisgültigkeit in Tagen, die eine Art vorschlägt (N-21). Nie gesetzt
 * (`undefined`) heißt: beim Angebot 30 Tage — so gilt die Vorgabe auch bei
 * Mandanten, deren Einstellungen vor dem Nachtrag entstanden sind. `null` heißt
 * ausdrücklich keine.
 */
export function preisgueltigkeitTage(kind: DocumentKind, optionen: DocumentKindOptions | null | undefined): number | null {
  if (!hatKopffeld(kind, 'priceValidity')) return null
  const wert = optionen?.priceValidityDays
  if (wert === undefined) return kind === 'OFFER' ? 30 : null
  return wert
}

/** Die elf Klauseln der Incoterms® 2020. */
export const INCOTERMS = ['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP', 'FAS', 'FOB', 'CFR', 'CIF'] as const
export type Incoterm = (typeof INCOTERMS)[number]

/** Wie ein Liefertermin gemeint ist (N-20). */
export const DELIVERY_DATE_TYPES = ['DATE', 'UNTIL', 'APPROX', 'FIXED', 'WEEK', 'PERIOD', 'TEXT'] as const
export type DeliveryDateType = (typeof DELIVERY_DATE_TYPES)[number]

/** Ein Zahlungstermin (N-22). `percent: null` ist der Rest — nur als letzte Zeile. */
export interface PaymentScheduleEntry {
  text: string
  percent: number | null
}
export const MAX_PAYMENT_SCHEDULE_ENTRIES = 10

export function capabilitiesOf(kind: DocumentKind): DocumentKindCapabilities {
  return DOCUMENT_KIND_CAPABILITIES[kind]
}

export function isDocumentKind(kandidat: string): kandidat is DocumentKind {
  return (DOCUMENT_KINDS as readonly string[]).includes(kandidat)
}

/** `correspondence.invoice.update` — das Recht einer Belegart für ein Verb. */
export function documentPermission(kind: DocumentKind, verb: DocumentVerb): string {
  return `correspondence.${DOCUMENT_KIND_CAPABILITIES[kind].permissionGroup}.${verb}`
}

/**
 * Welche Arten darf diese Rechteliste mit diesem Verb? Die Liste filtert danach,
 * das Anlegen-Menü auch — **dieselbe** Funktion auf beiden Seiten.
 */
export function documentKindsWith(verb: DocumentVerb, gehalten: readonly string[] | undefined): DocumentKind[] {
  if (!gehalten) return []
  return DOCUMENT_KINDS.filter((k) => gehalten.includes(documentPermission(k, verb)))
}

/** Das Feldrecht auf Preise, Rabatte, Summen und Zahlungen (10.3). */
export const DOCUMENT_PRICE_PERMISSION = 'correspondence.price.view'

/**
 * Zeigt ein Beleg dieser Art überhaupt Preise? Die Art (Preisanfrage nie), die
 * Belegart-Einstellung (Lieferschein) und — getrennt davon — das Feldrecht des
 * Lesers entscheiden. Diese Funktion kennt die ersten beiden.
 */
export function kindShowsPrices(kind: DocumentKind, settingShowPrices: boolean): boolean {
  const preise = DOCUMENT_KIND_CAPABILITIES[kind].prices
  if (preise === 'no') return false
  if (preise === 'optional') return settingShowPrices
  return true
}

// ─── Übernahme (AP-32 8.2) ───────────────────────────────────────────────────

/**
 * Nachtrag 9 (N-75): **Jeder Beleg lässt sich in jede Art umwandeln** — auch über die
 * Richtung hinweg (Angebot → Bestellung, Preisanfrage → Angebot) und zurück.
 *
 * Ausgenommen sind nur die zwei Arten, die **mehr tun als einen Beleg anlegen**:
 * Das Storno hebt die Rechnung auf, aus der es entsteht (8.3), die Mahnung setzt die
 * Mahnstufe der gemahnten Rechnungen. Beide haben ihren eigenen Weg; als **Quelle**
 * dürfen sie umgewandelt werden. Was die Zielart nicht führen kann, passt der Dienst
 * beim Übernehmen an (`passeZeilenAn`).
 */
export const DOCUMENT_TRANSFER_EXCLUDED_TARGETS: readonly DocumentKind[] = ['CANCELLATION', 'DUNNING']

/** Die Zielarten je Quelle — die eigene Art (Kopie) zuerst, dann in der Reihenfolge von `DOCUMENT_KINDS`. */
export const DOCUMENT_TRANSFERS: Readonly<Record<DocumentKind, readonly DocumentKind[]>> = DOCUMENT_KINDS.reduce(
  (tabelle, von) => {
    tabelle[von] = [von, ...DOCUMENT_KINDS.filter((k) => k !== von)].filter((k) => !DOCUMENT_TRANSFER_EXCLUDED_TARGETS.includes(k))
    return tabelle
  },
  {} as Record<DocumentKind, readonly DocumentKind[]>,
)

export function canTransfer(von: DocumentKind, nach: DocumentKind): boolean {
  return DOCUMENT_TRANSFERS[von].includes(nach)
}

// ─── Grenzen (AP-32 15.) ─────────────────────────────────────────────────────

// Die Preiseinheit (4.4.1) folgt derselben Regel wie am Artikel — seit dem
// AP-28-Nachtrag N-12 eine freie ganze Zahl: `price-unit.ts`.
export const MAX_DOCUMENT_LINES = 500
export const MAX_CLIPBOARD_ITEMS = 200
export const CLIPBOARD_MAX_AGE_DAYS = 30
/** Zeilentypen, die ein Foto tragen dürfen (5.9). */
export const LINE_TYPES_WITH_IMAGE: readonly DocumentLineType[] = ['ARTICLE', 'TEXT']
/** Zeilentypen mit Menge und Preis. */
export const VALUE_LINE_TYPES: readonly DocumentLineType[] = ['ARTICLE']
export const MAX_LINE_IMAGE_BYTES = 10 * 1024 * 1024
/** Nachtrag 4: Dateien an Positionen — je Datei, je Zeile und zusammen im Mailanhang. */
export const MAX_LINE_FILE_BYTES = 25 * 1024 * 1024
export const MAX_FILES_PER_LINE = 10
export const MAX_MAIL_ATTACHMENT_BYTES = 35 * 1024 * 1024
/** Erlaubte Endungen und der Typ, unter dem sie abgelegt werden — Zeichnungen, Modelle, Dokumente. */
export const LINE_FILE_TYPES: Readonly<Record<string, string>> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  dxf: 'image/vnd.dxf',
  dwg: 'image/vnd.dwg',
  step: 'model/step',
  stp: 'model/step',
  igs: 'model/iges',
  iges: 'model/iges',
  stl: 'model/stl',
  zip: 'application/zip',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
  csv: 'text/csv',
  /** AP-37 Detailblatt K 4.2 (K-26) — Teil-LV an Nachunternehmer; geprüft wird die XML-Wurzel. */
  onlv: 'application/xml',
  x83: 'application/xml',
}
export const MAX_DUNNING_LEVEL = 3

// ─── Positionsentwurf — der Vertrag der Zwischenablage (AP-32 5.7.1) ─────────

/**
 * **Eine Hauptposition samt Unterpositionen**, so wie sie in die Ablage geht
 * und aus ihr herauskommt. Ein fremdes Modul (Projektmanagement, Regiebericht)
 * legt genau diese Form ab — es braucht dafür weder Belegtabellen noch
 * Positionslogik.
 *
 * Beträge sind **Zahlen mit festen Nachkommastellen** (Menge 3, Preis 2); der
 * Server rundet nicht still, er prüft.
 */
export interface PositionDraft {
  lineType: DocumentLineType
  articleId?: string | null
  articleNumber?: string | null
  articleName?: string | null
  unitCode?: string | null
  /** Nachtrag 2: Menge in einer Alternativeinheit — Angabe, kein Rechenweg. */
  altQuantity?: number | null
  altUnitCode?: string | null
  articleTaxRate?: number | null
  description?: string | null
  /** Nachtrag 12 (N-93): einleitender Text **vor** der Position; `description` steht darunter. */
  introText?: string | null
  /** Nachtrag 12 (N-94): Auftragsnummer nur dieser Position — leer heißt „wie im Beleg". */
  customerReference?: string | null
  /** Liefertermin nur dieser Position, `JJJJ-MM-TT`. */
  deliveryDate?: string | null
  deliveryNote?: string | null
  quantity?: number
  unitPrice?: number
  priceUnit?: number
  discountPercent?: number | null
  /** Wird beim Einfügen neu aufgelöst (5.8 Schritt 4) — der Zielbeleg entscheidet. */
  taxRate?: number | null
  isOptional?: boolean
  isAlternative?: boolean
  subMode?: SubPositionMode | null
  projectId?: string | null
  projectNumber?: string | null
  projectPositionId?: string | null
  projectPositionNumber?: string | null
  projectPositionName?: string | null
  /** Nur bei Herkunft aus einem Beleg (5.8 Schritt 7). */
  sourceLineId?: string | null
  /**
   * AP-35 Phase G (Detailblatt 5 5.4): Herkunft außerhalb des Belegwesens —
   * das Festschreiben meldet sie an ihr Modul zurück.
   */
  sourceRef?: { kind: 'PROJECT_BOM_LINE' | 'PROJECT_SALES_LINE' | 'CALCULATION_POSITION' | 'CALCULATION_REQUEST_LINE' | 'SITE_REPORT_LINE'; id: string } | null
  /**
   * AP-37 Detailblatt O 6.6: gespeicherte Dateien, die als **Kopie** an die Belegzeile
   * kommen (`DocumentLineFile`). Gelesen nur von `entwurfMitZeilen` — das Einfügen aus
   * der Ablage übergeht das Feld.
   */
  files?: { storagePath: string; fileName: string }[]
  /** Unterpositionen — genau eine Ebene (5.2). Kinder tragen selbst keine. */
  children?: PositionDraft[]
}

export type ClipboardSourceKind =
  | 'DOCUMENT_LINE'
  | 'PROJECT_POSITION'
  | 'ARTICLE'
  | 'MANUAL'
  | 'PROJECT_BOM_LINE'
  | 'PROJECT_SALES_LINE'
  /** AP-37 4.14: eine Kalkulationsposition — Belegform plus `projectExtras` (Material, Arbeitsschritte). */
  | 'CALCULATION_POSITION'
  /** AP-35 J (Blatt 12 5.1): eine LV-Position aus dem LV eines Projekts. */
  | 'LV_ITEM'
  /** AP-37 Nachtrag 4 (N4-2): eine Kostenzeile des Kalkulationsblatts. */
  | 'CALCULATION_COST_LINE'
  /** AP-27 7.: eine Zeile eines unterschriebenen Regieberichts — nur in eine Rechnung. */
  | 'SITE_REPORT_LINE'

export interface ClipboardItemView {
  id: string
  sourceKind: ClipboardSourceKind
  sourceId: string | null
  /** Anzeige: woher („RE2026-0042, Pos. 3"). */
  sourceLabel: string | null
  position: PositionDraft
  /** Nur mit Preisrecht; ohne Recht fehlt das Feld (10.3). */
  amount?: number
  hasImage: boolean
  createdAt: string
}

// ─── Layout (AP-32 11.3) ─────────────────────────────────────────────────────

export type LayoutColumnKey =
  | 'pos'
  | 'quantity'
  | 'unit'
  | 'name'
  | 'unitPrice'
  | 'priceUnit'
  | 'discount'
  | 'taxRate'
  | 'amount'

export interface LayoutColumn {
  key: LayoutColumnKey
  /** Millimeter oder `*` für die Restbreite. */
  width: number | '*'
  align: 'left' | 'right' | 'center'
  hideWhenAllOne?: boolean
  hideWhenSingleRate?: boolean
}

export interface LayoutBlockBase {
  visible: boolean
}

export interface DocumentLayoutConfig {
  schemaVersion: 1
  page: { size: 'A4'; marginTop: number; marginBottom: number; marginLeft: number; marginRight: number }
  type: {
    family: string
    sizeBody: number
    sizeTable: number
    sizeSmall: number
    sizeSubject: number
    lineHeight: number
  }
  blocks: {
    senderLine: LayoutBlockBase & { x: number; y: number; width: number; size: number }
    addressField: LayoutBlockBase & { x: number; y: number; width: number; height: number; addressStartY: number }
    infoBlock: LayoutBlockBase & { x: number; y: number; width: number; align: 'left' | 'right'; rows: string[] }
    subject: LayoutBlockBase & { y: number; bold: boolean }
    headerText: LayoutBlockBase
    logo: LayoutBlockBase & { x: number; y: number; width: number }
    totals: { align: 'left' | 'right'; width: number }
    paymentBlock: LayoutBlockBase & { qr: boolean }
    footerText: LayoutBlockBase
    signature: LayoutBlockBase & { text: string }
    pageFooter: LayoutBlockBase & { y: number; size: number; text: string }
    foldMarks: LayoutBlockBase & { hole: boolean }
  }
  table: {
    columns: LayoutColumn[]
    headerRule: boolean
    zebra: boolean
    subIndentMm: number
    repeatHeader: boolean
    groupSum: 'below' | 'none'
    carryOver: boolean
    lineImages: LayoutBlockBase & { maxWidthMm: number; maxHeightMm: number }
  }
  colors: { accent: string | null }
}

/** Die Zeilen, die der Informationsblock kennt — ein Block braucht eine Datenquelle (11.3). */
export const INFO_BLOCK_ROWS = [
  'documentNumber',
  'documentDate',
  'customerNumber',
  'reference',
  'project',
  'servicePeriod',
  'dueDate',
  'handler',
  'phone',
  'email',
] as const

/**
 * Der Standardvorschlag nach DIN 5008 Form B (11.1). Jede Zahl ist der
 * Vorgabewert, auf den „Auf Standard zurücksetzen" zurückgeht.
 */
export const DEFAULT_DOCUMENT_LAYOUT: DocumentLayoutConfig = {
  schemaVersion: 1,
  page: { size: 'A4', marginTop: 20, marginBottom: 20, marginLeft: 25, marginRight: 20 },
  type: { family: 'Inter', sizeBody: 9.5, sizeTable: 9, sizeSmall: 6.5, sizeSubject: 12.5, lineHeight: 1.38 },
  blocks: {
    senderLine: { visible: true, x: 25, y: 57, width: 85, size: 6.5 },
    addressField: { visible: true, x: 25, y: 45, width: 85, height: 45, addressStartY: 62.7 },
    infoBlock: {
      visible: true,
      x: 125,
      y: 50,
      width: 65,
      align: 'left',
      rows: ['documentNumber', 'documentDate', 'customerNumber', 'reference', 'project', 'servicePeriod', 'dueDate', 'handler', 'email'],
    },
    subject: { visible: true, y: 98.5, bold: true },
    headerText: { visible: true },
    logo: { visible: true, x: 150, y: 12, width: 40 },
    totals: { align: 'right', width: 78 },
    paymentBlock: { visible: true, qr: true },
    footerText: { visible: true },
    signature: { visible: true, text: 'Mit freundlichen Grüßen' },
    pageFooter: { visible: true, y: 279, size: 6.5, text: '{firma} · UID {uid} · IBAN {iban} · Seite {seite}/{seiten}' },
    foldMarks: { visible: true, hole: true },
  },
  table: {
    columns: [
      { key: 'pos', width: 10, align: 'left' },
      { key: 'quantity', width: 16, align: 'right' },
      { key: 'unit', width: 12, align: 'left' },
      { key: 'name', width: '*', align: 'left' },
      { key: 'unitPrice', width: 22, align: 'right' },
      { key: 'priceUnit', width: 10, align: 'right', hideWhenAllOne: true },
      { key: 'discount', width: 14, align: 'right' },
      { key: 'taxRate', width: 11, align: 'right', hideWhenSingleRate: true },
      { key: 'amount', width: 24, align: 'right' },
    ],
    headerRule: true,
    zebra: false,
    subIndentMm: 6,
    repeatHeader: true,
    groupSum: 'below',
    carryOver: true,
    lineImages: { visible: true, maxWidthMm: 60, maxHeightMm: 45 },
  },
  colors: { accent: null },
}

/** Blattmaße A4 in Millimetern. */
export const DOCUMENT_PAGE_MM = { width: 210, height: 297 } as const
/** Näher als diese Grenze an der Blattkante druckt kein Drucker sauber (15.). */
export const DOCUMENT_PAGE_EDGE_MM = 5

// ─── Einstellungen je Belegart ───────────────────────────────────────────────

/** Die Zusatzwerte einer Belegart, die nicht jede Art braucht (`DocumentSetting.options`). */
export interface DocumentKindOptions {
  /** Rechnung: Kleinbetragsrechnung bis zu diesem Bruttobetrag (7.4). */
  smallInvoiceLimit?: number
  /** Rechnung: ab diesem Bruttobetrag ist die UID des Empfängers Pflicht (7.4). */
  recipientVatIdFrom?: number
  /** Mahnung: je Stufe Gebühr, Zinssatz (Prozent p. a.) und Zahlungsfrist in Tagen (8.5). */
  dunningLevels?: { fee: number; interestRate: number; graceDays: number }[]
  /** Angebot, Auftragsbestätigung: Preisgültigkeit in Tagen ab Belegdatum (Nachtrag N-21). */
  priceValidityDays?: number | null
  /** Lieferschein (N-58): Verrechnungsablauf fix — nur freigegebene Lieferscheine werden zu Rechnungen. */
  requireBillingRelease?: boolean
}

/** Die Textbaustein-Plätze (AP-32 4.1). */
export const DOCUMENT_TEXT_SLOTS = [
  'HEADER',
  'FOOTER',
  'TAX_NOTE_REVERSE_CHARGE',
  'TAX_NOTE_INTRA_EU',
  'TAX_NOTE_EXPORT',
  'TAX_NOTE_SMALL_BUSINESS',
  'TAX_NOTE_EXEMPT',
  'PAYMENT',
  'PAYMENT_DISCOUNT',
  'DUNNING_1',
  'DUNNING_2',
  'DUNNING_3',
  'EMAIL_SUBJECT',
  'EMAIL_BODY',
] as const
export type DocumentTextSlot = (typeof DOCUMENT_TEXT_SLOTS)[number]

export function taxNoteSlot(procedure: TaxProcedure): DocumentTextSlot | null {
  return procedure === 'STANDARD' ? null : (`TAX_NOTE_${procedure}` as DocumentTextSlot)
}
